/**
 * Amplitude Event Catalog — single source of truth for all tracked events.
 *
 * Every Amplitude event name and its expected properties are defined here.
 * Components import the event name constants to prevent typos and keep
 * the event taxonomy discoverable in one place.
 *
 * Naming convention: `{feature}_{action}` in snake_case.
 */

/* ──────────────────────────────────────────────────────────────────────
 * 1. Core Project Funnel
 * ────────────────────────────────────────────────────────────────────── */

export const PROJECT_CREATED = "project_created" as const;
export const PROJECT_OPENED = "project_opened" as const;
export const PROJECT_DELETED = "project_deleted" as const;

export const VIDEO_UPLOAD_STARTED = "video_upload_started" as const;
export const VIDEO_UPLOAD_COMPLETED = "video_upload_completed" as const;
export const VIDEO_UPLOAD_FAILED = "video_upload_failed" as const;
export const VIDEO_TYPE_SELECTED = "video_type_selected" as const;
export const CONTINUE_TO_ANALYSIS_CLICKED =
  "continue_to_analysis_clicked" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 2. Analysis & Optimization
 * ────────────────────────────────────────────────────────────────────── */

export const OPTIMIZE_PAGE_VIEWED = "optimize_page_viewed" as const;
export const VARIANTS_GENERATED = "variants_generated" as const;
export const VARIANTS_GENERATION_FAILED = "variants_generation_failed" as const;
export const PLATFORM_TOGGLED = "platform_toggled" as const;
export const PLATFORM_SWITCHED = "platform_switched" as const;
export const VARIANT_SELECTED = "variant_selected" as const;
export const METADATA_SAVED = "metadata_saved" as const;
export const VOICE_BLEND_CHANGED = "voice_blend_changed" as const;
export const ANALYSIS_COMPLETED = "analysis_completed" as const;
export const CONTINUE_TO_OPTIMIZE_CLICKED =
  "continue_to_optimize_clicked" as const;

export const JOURNEY_STEP_REACHED = "journey_step_reached" as const;
export const WELCOME_OVERLAY_DISMISSED = "welcome_overlay_dismissed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 3. Thumbnail Studio (extends existing events)
 * ────────────────────────────────────────────────────────────────────── */

export const THUMBNAIL_FRAME_SELECTED = "thumbnail_frame_selected" as const;
export const THUMBNAIL_STUDIO_OPENED = "thumbnail_studio_opened" as const;
export const THUMBNAIL_BEST_FRAME_AUTO_SELECTED =
  "thumbnail_best_frame_auto_selected" as const;
export const THUMBNAIL_GENERATION_STARTED =
  "thumbnail_generation_started" as const;
export const THUMBNAIL_GENERATION_COMPLETED =
  "thumbnail_generation_completed" as const;
export const THUMBNAIL_EXPORTED = "thumbnail_exported" as const;
export const THUMBNAIL_VARIANT_SELECTED = "thumbnail_variant_selected" as const;
export const THUMBNAIL_TEXT_TEMPLATE_APPLIED =
  "thumbnail_text_template_applied" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 4. Publishing
 * ────────────────────────────────────────────────────────────────────── */

export const PUBLISH_PANEL_OPENED = "publish_panel_opened" as const;
export const PUBLISH_ACCOUNT_SELECTED = "publish_account_selected" as const;
export const PUBLISH_ACCOUNT_DESELECTED = "publish_account_deselected" as const;
export const PUBLISH_GROUP_SELECTED = "publish_group_selected" as const;
export const PUBLISH_STARTED = "publish_started" as const;
export const PUBLISH_COMPLETED = "publish_completed" as const;
export const PUBLISH_FAILED = "publish_failed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 5. Auto-Clips (Shorts)
 * ────────────────────────────────────────────────────────────────────── */

export const AUTOCLIPS_BATCH_LOADED = "autoclips_batch_loaded" as const;
export const AUTOCLIPS_CLIP_DOWNLOADED = "autoclips_clip_downloaded" as const;
export const AUTOCLIPS_CLIP_DOWNLOAD_FAILED =
  "autoclips_clip_download_failed" as const;
export const AUTOCLIPS_CAPTION_STYLE_CHANGED =
  "autoclips_caption_style_changed" as const;
export const AUTOCLIPS_REFRAME_CHANGED = "autoclips_reframe_changed" as const;
export const AUTOCLIPS_METADATA_COPIED = "autoclips_metadata_copied" as const;
export const AUTOCLIPS_TRIM_OPENED = "autoclips_trim_opened" as const;
export const AUTOCLIPS_TRIM_APPLIED = "autoclips_trim_applied" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 6. Channel Management
 * ────────────────────────────────────────────────────────────────────── */

export const CHANNEL_CONNECT_STARTED = "channel_connect_started" as const;
export const CHANNEL_SWITCHED = "channel_switched" as const;
export const CHANNEL_DISCONNECTED = "channel_disconnected" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 8. Seeds / Billing
 * ────────────────────────────────────────────────────────────────────── */

export const SEEDS_PAGE_VIEWED = "seeds_page_viewed" as const;
export const SEEDS_PACK_CLICKED = "seeds_pack_clicked" as const;
export const INSUFFICIENT_SEEDS_SHOWN = "insufficient_seeds_shown" as const;
export const INSUFFICIENT_SEEDS_GET_MORE_CLICKED =
  "insufficient_seeds_get_more_clicked" as const;
export const INSUFFICIENT_SEEDS_DISMISSED =
  "insufficient_seeds_dismissed" as const;

export const PRICING_PAGE_VIEWED = "pricing_page_viewed" as const;
export const PLAN_VIEWED = "plan_viewed" as const;
export const CHECKOUT_SESSION_CREATED = "checkout_session_created" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 9. Settings
 * ────────────────────────────────────────────────────────────────────── */

export const SETTINGS_PROFILE_UPDATED = "settings_profile_updated" as const;
export const SETTINGS_LANGUAGE_CHANGED = "settings_language_changed" as const;
export const SETTINGS_PAGE_VIEWED = "settings_page_viewed" as const;
export const CHANNEL_CONNECTION_FLOW_STARTED =
  "channel_connection_flow_started" as const;
export const CHANNEL_CONNECTION_COMPLETED =
  "channel_connection_completed" as const;
export const ACCOUNT_SWITCHED = "account_switched" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 10. Dashboard & Navigation
 * ────────────────────────────────────────────────────────────────────── */

export const DASHBOARD_QUICK_ACTION_CLICKED =
  "dashboard_quick_action_clicked" as const;
export const DASHBOARD_RECENT_PROJECT_CLICKED =
  "dashboard_recent_project_clicked" as const;
export const SIDEBAR_ITEM_CLICKED = "sidebar_item_clicked" as const;
export const CONNECT_CHANNEL_BANNER_CLICKED =
  "connect_channel_banner_clicked" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 11. Error & Friction Events
 * ────────────────────────────────────────────────────────────────────── */

export const ERROR_PAGE_SHOWN = "error_page_shown" as const;
export const ERROR_RETRY_CLICKED = "error_retry_clicked" as const;
export const LIMIT_MODAL_SHOWN = "limit_modal_shown" as const;
export const LIMIT_MODAL_DISMISSED = "limit_modal_dismissed" as const;
export const MOBILE_FILE_PICKER_FAILED = "mobile_file_picker_failed" as const;
export const UPLOAD_ABANDONED = "upload_abandoned" as const;
export const ANALYSIS_ABANDONED = "analysis_abandoned" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 12. Auth Lifecycle
 * ────────────────────────────────────────────────────────────────────── */

export const SIGNUP_COMPLETED = "signup_completed" as const;
export const LOGIN_COMPLETED = "login_completed" as const;
export const LOGOUT = "logout" as const;
export const SIGNUP_METHOD_SELECTED = "signup_method_selected" as const;
export const LOGIN_METHOD_SELECTED = "login_method_selected" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 13. Revenue & Payments (server-side via amplitude-server.ts)
 * ────────────────────────────────────────────────────────────────────── */

export const PURCHASE_COMPLETED = "purchase_completed" as const;
export const SUBSCRIPTION_STARTED = "subscription_started" as const;
export const SUBSCRIPTION_CANCELLED = "subscription_cancelled" as const;
export const SUBSCRIPTION_RENEWED = "subscription_renewed" as const;
export const CHECKOUT_STARTED = "checkout_started" as const;
export const CHECKOUT_ABANDONED = "checkout_abandoned" as const;
export const PAYMENT_FAILED = "payment_failed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 14. Dubbing & Translation
 * ────────────────────────────────────────────────────────────────────── */

export const DUBBING_STARTED = "dubbing_started" as const;
export const DUBBING_COMPLETED = "dubbing_completed" as const;
export const DUBBING_FAILED = "dubbing_failed" as const;
export const TRANSLATE_STARTED = "translate_started" as const;
export const TRANSLATE_COMPLETED = "translate_completed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 15. Captions
 * ────────────────────────────────────────────────────────────────────── */

export const CAPTIONS_GENERATED = "captions_generated" as const;
export const CAPTIONS_STYLE_CHANGED = "captions_style_changed" as const;
export const CAPTIONS_DOWNLOADED = "captions_downloaded" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 16. Video Ideas & Channel Intelligence
 * ────────────────────────────────────────────────────────────────────── */

export const VIDEO_IDEA_GENERATED = "video_idea_generated" as const;
export const VIDEO_IDEA_SAVED = "video_idea_saved" as const;
export const CHANNEL_INVESTIGATION_STARTED =
  "channel_investigation_started" as const;
export const CHANNEL_INVESTIGATION_COMPLETED =
  "channel_investigation_completed" as const;
export const CHANNEL_DESCRIPTION_GENERATED =
  "channel_description_generated" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 17. Midroll Ads
 * ────────────────────────────────────────────────────────────────────── */

export const MIDROLL_ANALYSIS_STARTED = "midroll_analysis_started" as const;
export const MIDROLL_ANALYSIS_COMPLETED = "midroll_analysis_completed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 18. Precision Trim
 * ────────────────────────────────────────────────────────────────────── */

export const PRECISION_TRIM_OPENED = "precision_trim_opened" as const;
export const PRECISION_TRIM_COMPLETED = "precision_trim_completed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 19. Engagement & Social
 * ────────────────────────────────────────────────────────────────────── */

export const SHARE_CLICKED = "share_clicked" as const;
export const REFERRAL_LINK_COPIED = "referral_link_copied" as const;
export const FEATURE_DISCOVERED = "feature_discovered" as const;
export const SEARCH_PERFORMED = "search_performed" as const;

/* ──────────────────────────────────────────────────────────────────────
 * 20. Cleaver Dubbing
 * ────────────────────────────────────────────────────────────────────── */

export const CLEAVER_DUBBING_STARTED = "cleaver_dubbing_started" as const;
export const CLEAVER_DUBBING_COMPLETED = "cleaver_dubbing_completed" as const;
